import React, { useState, useEffect } from 'react';
import { Modal, Table, Badge, Button, Spinner } from 'flowbite-react';
import { HiX, HiClock, HiBell, HiCheck, HiClipboard } from 'react-icons/hi';
import { format } from 'date-fns';
import { apiService } from '../services/api';
import { registrationService } from '../services/registrationService';
import { screenService } from '../services/screenService';
import type { Registration } from '../types';

interface NotificationHistory {
  id: string;
  callNumber: string;
  lineUserId: string;
  notificationType: string;
  context: string;
  room: string;
  name: string;
  notifiedAt: string;
  screenId: string;
  createdAt: string;
}

interface NotificationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineUserId?: string;
}

const NotificationHistoryModal: React.FC<NotificationHistoryModalProps> = ({
  isOpen,
  onClose,
  lineUserId,
}) => {
  const [histories, setHistories] = useState<NotificationHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationsError, setRegistrationsError] = useState<string | null>(null);
  const [copiedUid, setCopiedUid] = useState(false);
  const [screenImages, setScreenImages] = useState<Record<string, string>>({});
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && lineUserId) {
      fetchNotificationHistory();
      fetchRegistrations();
    }
  }, [isOpen, lineUserId]);

  const fetchNotificationHistory = async () => {
    if (!lineUserId) return;

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('lineUserId', lineUserId);

      const response: any = await apiService.get(`/v1/admin/notification-history?${params.toString()}`);
      if (response?.data) {
        setHistories(response.data);

        // screenId が存在するものだけ画像取得
        const uniqueScreenIds: string[] = [...new Set(
          (response.data as NotificationHistory[])
            .map((h) => h.screenId)
            .filter((id): id is string => Boolean(id))
        )];

        const imageMap: Record<string, string> = {};
        await Promise.allSettled(
          uniqueScreenIds.map(async (screenId) => {
            try {
              const screen = await screenService.getScreenById(screenId);
              imageMap[screenId] = screen.imageUrl || '';
            } catch {
              // 画像取得失敗は無視（'-' 表示のまま）
            }
          })
        );
        setScreenImages(imageMap);
      }
    } catch (err: any) {
      console.error('Failed to fetch notification history:', err);
      setError('通知履歴の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    if (!lineUserId) return;
    setRegistrationsLoading(true);
    setRegistrationsError(null);
    try {
      const response = await registrationService.getRegistrations({
        page: 1,
        limit: 100,
        searchType: 'lineUserId',
        searchValue: lineUserId,
      });
      setRegistrations(
        response.items.slice().sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );
    } catch {
      setRegistrationsError('登録履歴の取得に失敗しました');
    } finally {
      setRegistrationsLoading(false);
    }
  };

  const getNotificationTypeBadge = (type: string) => {
    const typeConfig: Record<string, { color: string; label: string }> = {
      'not-called': { color: 'gray', label: '未呼び出し' },
      calling: { color: 'blue', label: '呼び出し中' },
      waiting: { color: 'yellow', label: '呼び出し待機中' },
      called: { color: 'green', label: '呼び出し済み' },
    };
    const config = typeConfig[type] || { color: 'gray', label: type };
    return <Badge color={config.color as any}>{config.label}</Badge>;
  };

  const getRegistrationStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      'not-called': { color: 'gray', label: '未呼び出し' },
      'waiting':    { color: 'yellow', label: '待機中' },
      'calling':    { color: 'blue', label: '呼び出し中' },
      'called':     { color: 'green', label: '呼び出し済み' },
    };
    const c = config[status] || { color: 'gray', label: status };
    return <Badge color={c.color as any}>{c.label}</Badge>;
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy/MM/dd HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  return (
    <>
    <Modal show={isOpen} onClose={onClose} size="5xl">
      <Modal.Header>
        <div className="flex items-center gap-2 flex-wrap">
          <HiBell className="h-5 w-5" />
          <span>ゲスト調査</span>
          {lineUserId && (
            <>
              <span className="text-sm font-mono text-gray-600 break-all">{lineUserId}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(lineUserId);
                  setCopiedUid(true);
                  setTimeout(() => setCopiedUid(false), 1500);
                }}
                className="text-gray-400 hover:text-gray-600"
                title="コピー"
              >
                {copiedUid
                  ? <HiCheck className="h-4 w-4 text-green-500" />
                  : <HiClipboard className="h-4 w-4" />
                }
              </button>
            </>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* 登録履歴セクション */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
            <span>📋</span> 登録履歴
          </h3>
          {registrationsLoading ? (
            <div className="flex justify-center py-4"><Spinner size="md" /></div>
          ) : registrationsError ? (
            <div className="text-red-500 text-sm py-2">{registrationsError}</div>
          ) : registrations.length === 0 ? (
            <div className="text-gray-500 text-sm py-2">登録履歴がありません</div>
          ) : (
            <div className="overflow-x-auto">
              <Table striped>
                <Table.Head>
                  <Table.HeadCell>番号</Table.HeadCell>
                  <Table.HeadCell>ステータス</Table.HeadCell>
                  <Table.HeadCell>登録日時</Table.HeadCell>
                  <Table.HeadCell>呼出日時</Table.HeadCell>
                  <Table.HeadCell>有効</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {registrations.map((reg) => (
                    <Table.Row key={reg.id}>
                      <Table.Cell className="font-medium">{reg.callNumber}</Table.Cell>
                      <Table.Cell>{getRegistrationStatusBadge(reg.status)}</Table.Cell>
                      <Table.Cell>{formatDateTime(reg.createdAt)}</Table.Cell>
                      <Table.Cell>{reg.calledAt ? formatDateTime(reg.calledAt) : '-'}</Table.Cell>
                      <Table.Cell>
                        <Badge color={reg.isActive ? 'success' : 'gray'}>
                          {reg.isActive ? '有効' : '無効'}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>

        {/* 通知履歴セクション */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
            <span>🔔</span> 通知履歴
          </h3>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="xl" />
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-8">{error}</div>
          ) : histories.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              通知履歴がありません
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table striped>
                <Table.Head>
                  <Table.HeadCell>通知日時</Table.HeadCell>
                  <Table.HeadCell>呼び出し番号</Table.HeadCell>
                  <Table.HeadCell>通知タイプ</Table.HeadCell>
                  <Table.HeadCell>場所</Table.HeadCell>
                  <Table.HeadCell>名称</Table.HeadCell>
                  <Table.HeadCell>トリガー画像</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {histories.map((history) => (
                    <Table.Row key={history.id}>
                      <Table.Cell>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <HiClock className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{formatDateTime(history.notifiedAt).split(' ')[0]}</span>
                          </div>
                          <span className="text-sm text-gray-600 ml-5">{formatDateTime(history.notifiedAt).split(' ')[1]}</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="font-medium">
                        {history.callNumber}
                      </Table.Cell>
                      <Table.Cell>
                        {getNotificationTypeBadge(history.notificationType)}
                      </Table.Cell>
                      <Table.Cell>{history.room || '-'}</Table.Cell>
                      <Table.Cell>{history.name || '-'}</Table.Cell>
                      <Table.Cell>
                        {history.screenId && screenImages[history.screenId] ? (
                          <img
                            src={screenImages[history.screenId]}
                            alt="トリガー画像"
                            className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                            onClick={() => setEnlargedImage(screenImages[history.screenId])}
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose}>
          <HiX className="mr-2 h-4 w-4" />
          閉じる
        </Button>
      </Modal.Footer>
    </Modal>

    {/* 画像拡大オーバーレイ */}
    {enlargedImage && (
      <div
        className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999]"
        onClick={() => setEnlargedImage(null)}
      >
        <img
          src={enlargedImage}
          alt="拡大表示"
          className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
};

export default NotificationHistoryModal;